import { skyGradientCss } from '../../shared/conditions'
import type { SkyScene } from '../../sky/scene'
import { heroVeil } from '../v4/hero/veil'

/**
 * The look of a place's sky (gradient, material, every colour variable), kept per place so the
 * next visit paints the right theme in the very first frame: index.html applies it from an inline
 * script before any bundle arrives, and the theme is never decided again afterwards.
 */
export const BOOT_SKY_KEY = bootSkyKey()

/** Demo scenes (`?mock=<name>`) keep their own skies (index.html reads the same key), so a demo never themes a real visit. */
function bootSkyKey(): string {
  const demo = typeof location === 'undefined' ? null : /[?&]mock=([^&]*)/.exec(location.search)
  return demo ? `wf2-boot-sky-demo-${demo[1]}` : 'wf2-boot-sky'
}
const KEEP = 6

export interface BootSky {
  /** CSS gradient of the sky palette. */
  g: string
  /** Material: light or dark glass. */
  m: 'light' | 'dark'
  /** Palette top colour (theme-color). */
  top: string
  /** The :root custom properties of the scene (lib/skyVars). */
  v: Record<string, string>
  /** Saved at (ms). */
  t: number
  /** A 40 x 24 JPEG of the sky renderer's own picture of this gradient (data URL): the first paint starts on it (see `saveSkySnap`). */
  img?: string
  /** The picture's row averages as gradient stops (`#rrggbb,…`): what paints under the page before the app has (no image, so it is not "content"). */
  rows?: string
  /** The hero's veil for this sky ("alpha r g b"): index.html paints it under the boot sky so the app's own veil adds nothing at first paint. */
  vl?: string
}

/** The place key shared with index.html (same rounding as the report hints). */
export const bootKey = (latitude: number, longitude: number) => `${latitude.toFixed(3)},${longitude.toFixed(3)}`

/**
 * The sky gradient this place wore last time (null on a first visit). The map page paints it as
 * its ground in the very first frame, before any report is in, so it never opens on a guess.
 */
export function readBootGround(latitude: number, longitude: number): string | null {
  try {
    const all = JSON.parse(localStorage.getItem(BOOT_SKY_KEY) || 'null') as Record<string, BootSky> | null
    const g = all?.[bootKey(latitude, longitude)]?.g
    return typeof g === 'string' && g.startsWith('linear-gradient') ? g : null
  } catch {
    return null
  }
}

/** Whether this place's page wore light or dark glass last time (null on a first visit): the sun's side, without an ephemeris. */
export function readBootMaterial(latitude: number, longitude: number): 'light' | 'dark' | null {
  try {
    const all = JSON.parse(localStorage.getItem(BOOT_SKY_KEY) || 'null') as Record<string, BootSky> | null
    const m = all?.[bootKey(latitude, longitude)]?.m
    return m === 'light' || m === 'dark' ? m : null
  } catch {
    return null
  }
}

/**
 * The hero's foot (px from the top of the page) and height at this window size, for index.html's boot veil: the veil's tail is as
 * long as the hero is tall, and the first frame needs that before any layout. Kept for the six most recent window sizes.
 */
export function saveBootHero(foot: number, height: number): void {
  try {
    const all = JSON.parse(localStorage.getItem('wf2-boot-hero') || '{}') as Record<string, number[]>
    const k = `${innerWidth}x${innerHeight}`
    const v = [Math.round(foot), Math.round(height)]
    if (all[k]?.[0] === v[0] && all[k]?.[1] === v[1]) return
    delete all[k]
    all[k] = v
    for (const old of Object.keys(all).slice(0, -6)) delete all[old]
    localStorage.setItem('wf2-boot-hero', JSON.stringify(all))
  } catch {
    // Storage full or blocked: the next visit paints an estimate of the veil's size.
  }
}

export function saveBootSky(key: string, scene: SkyScene, vars: Record<string, string>, material: 'light' | 'dark'): void {
  try {
    const all = JSON.parse(localStorage.getItem(BOOT_SKY_KEY) || '{}') as Record<string, BootSky>
    const g = skyGradientCss(scene.palette)
    const prev = all[key]
    const veil = heroVeil(scene)
    const vl = `${veil.alpha} ${veil.rgb.join(' ')}`
    if (prev && prev.g === g && prev.m === material && prev.vl === vl && prev.v['--card-1'] === vars['--card-1'] && prev.v['--glass-fill'] === vars['--glass-fill']) return
    all[key] = { g, m: material, top: scene.palette.top, v: vars, vl, t: Date.now(), ...(prev && prev.g === g && prev.img ? { img: prev.img, rows: prev.rows } : {}) }
    const keys = Object.keys(all).sort((a, b) => all[b].t - all[a].t)
    for (const k of keys.slice(KEEP)) delete all[k]
    localStorage.setItem(BOOT_SKY_KEY, JSON.stringify(all))
  } catch {
    // Storage full or blocked: the next visit simply paints the neutral dusk first.
  }
}

/**
 * The sky renderer's picture of the sky now, kept beside the place's boot theme. The renderer paints the whole sky (haze,
 * clouds), the CSS gradient only its palette, and the two differ a lot: a reload that started on the gradient and then
 * faded the renderer in changed colour every time. index.html sets this picture as the boot background and the first
 * gradient layer wears it, so the first frame is the sky the page left and the live sky fades in over (almost) itself.
 * Kept only for the gradient it was taken under: a sky that has since changed starts on its palette, as before.
 */
export function saveSkySnap(key: string, gradient: string, img: string, rows: string): void {
  try {
    const all = JSON.parse(localStorage.getItem(BOOT_SKY_KEY) || 'null') as Record<string, BootSky> | null
    const entry = all?.[key]
    if (!all || !entry || entry.g !== gradient || entry.img === img) return
    entry.img = img
    entry.rows = rows
    localStorage.setItem(BOOT_SKY_KEY, JSON.stringify(all))
  } catch {
    // Storage full or blocked: the next visit starts on the gradient.
  }
}
